/* global document, HTMLElement, HTMLInputElement, KeyboardEvent */

import { detectActiveSheetHasCellContent } from './active-sheet-content.util';

export function overwriteDisclaimerMessage(sheetName: string): string {
    return `The active worksheet (“${sheetName}”) already has data. Fetching will overwrite cells from A1 downward (including any existing Trace report output on this sheet).`;
}

export const OVERWRITE_DISCLAIMER_UNKNOWN_SHEET =
    'Could not read the active worksheet. Fetch writes from cell A1 and may replace existing content on that sheet.';

export interface FetchOverwriteConfirmResult {
    proceed: boolean;
    writeToNewWorksheet: boolean;
}

/**
 * Office (especially Excel on the web) often blocks `window.confirm`. Uses an in-pane dialog instead.
 */
export function showOverwriteConfirmDialog(bodyMessage: string): Promise<{
    confirmed: boolean;
    addNewWorksheet: boolean;
}> {
    return new Promise((resolve) => {
        const root = document.getElementById('overwrite-confirm-dialog');
        const msgEl = document.getElementById('overwrite-confirm-message');
        const cancelBtn = document.getElementById('overwrite-confirm-cancel');
        const okBtn = document.getElementById('overwrite-confirm-ok');
        const backdrop = document.getElementById('overwrite-confirm-backdrop');
        const newSheetCheckbox = document.getElementById(
            'overwrite-confirm-new-sheet'
        ) as HTMLInputElement | null;

        if (!root || !msgEl || !cancelBtn || !okBtn) {
            resolve({ confirmed: false, addNewWorksheet: false });
            return;
        }

        const previousFocus =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        let settled = false;

        const finish = (confirmed: boolean, addNewWorksheet: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            root.classList.add('hidden');
            cancelBtn.removeEventListener('click', onCancel);
            okBtn.removeEventListener('click', onOk);
            backdrop?.removeEventListener('click', onCancel);
            document.removeEventListener('keydown', onKeyDown);
            previousFocus?.focus();
            resolve({ confirmed, addNewWorksheet });
        };

        const onCancel = () => finish(false, false);
        const onOk = () => {
            const addNew = newSheetCheckbox?.checked === true;
            finish(true, addNew);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                finish(false, false);
            }
        };

        if (newSheetCheckbox) {
            newSheetCheckbox.checked = false;
        }
        msgEl.textContent = `${bodyMessage}\n\nContinue with fetch?`;
        root.classList.remove('hidden');
        cancelBtn.addEventListener('click', onCancel);
        okBtn.addEventListener('click', onOk);
        backdrop?.addEventListener('click', onCancel);
        document.addEventListener('keydown', onKeyDown);
        okBtn.focus();
    });
}

/**
 * Blocks the fetch until the user confirms when the active sheet may be overwritten.
 * When the sheet is empty, proceeds without a dialog. When the overwrite dialog is shown,
 * the user may opt to add a new worksheet instead of overwriting the active sheet.
 */
export async function confirmFetchIfActiveSheetMayBeOverwritten(): Promise<FetchOverwriteConfirmResult> {
    const proceedNoDialog: FetchOverwriteConfirmResult = {
        proceed: true,
        writeToNewWorksheet: false,
    };

    try {
        const { sheetName, hasContent } = await detectActiveSheetHasCellContent();
        if (!hasContent) {
            return proceedNoDialog;
        }
        const { confirmed, addNewWorksheet } = await showOverwriteConfirmDialog(
            overwriteDisclaimerMessage(sheetName)
        );
        return {
            proceed: confirmed,
            writeToNewWorksheet: confirmed && addNewWorksheet,
        };
    } catch {
        const { confirmed, addNewWorksheet } = await showOverwriteConfirmDialog(
            OVERWRITE_DISCLAIMER_UNKNOWN_SHEET
        );
        return {
            proceed: confirmed,
            writeToNewWorksheet: confirmed && addNewWorksheet,
        };
    }
}
